/**
 * Size Selector Modal
 * Opens when incrementing quantity in cart for products with size options.
 * Lets the customer pick a size for the additional item.
 * Preserves TeeInBlue customization properties.
 */
if (!customElements.get('size-selector-modal')) {
  class SizeSelectorModal extends HTMLElement {
    static SIZE_KEYWORDS = ['size', 'maat', 'größe', 'grosse', 'grösse', 'taille', 'talla', 'tamanho', 'dimensione'];
    static cache = new Map();
    static CACHE_DURATION = 5 * 60 * 1000;

    constructor() { super(); this.isProcessing = false; }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.backdrop = this.querySelector('.size-modal__backdrop');
      this.sizesContainer = this.querySelector('[data-sizes]');
      this.loadingEl = this.querySelector('[data-loading]');
      this.errorEl = this.querySelector('[data-error]');
      this.titleEl = this.querySelector('.size-modal__title');
      this.descriptionEl = this.querySelector('.size-modal__description');
      this.querySelector('[data-close]')?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); this.close(); } });
    }

    /**
     * Open modal. Accepts a config object:
     * { productHandle, variantId, lineKey, properties }
     */
    async open(config) {
      this.currentConfig = config;
      this.currentProperties = config.properties || {};
      this.currentVariantId = config.variantId ? parseInt(config.variantId, 10) : null;

      this.showState('loading');
      this.dialog?.showModal();
      this.backdrop?.classList.add('active');
      document.body.style.overflow = 'hidden';

      try {
        var product = await this.fetchProduct(config.productHandle);
        this.renderSizes(product);
      } catch (e) {
        console.error('Size selector error:', e);
        this.showState('error');
      }
    }

    close() {
      this.dialog?.classList.add('dialog-closing');
      this.backdrop?.classList.remove('active');
      setTimeout(() => {
        this.dialog?.close();
        this.dialog?.classList.remove('dialog-closing');
        document.body.style.overflow = '';
      }, 150);
    }

    showState(s) {
      if (this.loadingEl) this.loadingEl.style.display = s === 'loading' ? 'block' : 'none';
      if (this.errorEl) this.errorEl.style.display = s === 'error' ? 'block' : 'none';
      if (this.sizesContainer) this.sizesContainer.style.display = s === 'sizes' ? 'grid' : 'none';
    }

    async fetchProduct(handle) {
      var cached = SizeSelectorModal.cache.get(handle);
      if (cached && Date.now() - cached.time < SizeSelectorModal.CACHE_DURATION) return cached.data;
      // Use .js endpoint (returns options as string array + variants with options array)
      var res = await fetch('/products/' + handle + '.js');
      if (!res.ok) throw new Error('Failed to fetch product');
      var data = await res.json();
      SizeSelectorModal.cache.set(handle, { data: data, time: Date.now() });
      return data;
    }

    /**
     * Get option name - handles both string and object format
     */
    getOptionName(opt) {
      if (!opt) return '';
      if (typeof opt === 'string') return opt;
      if (typeof opt === 'object' && opt.name) return opt.name;
      return '';
    }

    findSizeOptionIndex(product) {
      for (var i = 0; i < product.options.length; i++) {
        var name = this.getOptionName(product.options[i]).toLowerCase().trim();
        if (SizeSelectorModal.SIZE_KEYWORDS.includes(name)) return i;
      }
      return -1;
    }

    renderSizes(product) {
      var sizeIdx = this.findSizeOptionIndex(product);
      if (sizeIdx === -1) {
        console.error('Size option not found. Available options:', product.options.map(o => this.getOptionName(o)));
        this.showState('error');
        return;
      }

      // Find the current variant to determine non-size option values
      var currentVariant = null;
      if (this.currentVariantId) {
        currentVariant = product.variants.find(v => v.id === this.currentVariantId);
      }

      // Build signature of non-size options from current variant
      var currentSignature = null;
      if (currentVariant) {
        var sigParts = [];
        for (var i = 0; i < product.options.length; i++) {
          if (i === sizeIdx) continue;
          // .js endpoint: variants have options[] array; .json endpoint: variants have option1/option2/option3
          var val = currentVariant.options ? currentVariant.options[i] : currentVariant['option' + (i + 1)];
          sigParts.push(val || '');
        }
        currentSignature = sigParts.join('|');
      }

      // Filter variants: match non-size options, deduplicate by size value
      var seenSizes = new Set();
      var matchingVariants = product.variants.filter(function(v) {
        if (!v.available) return false;

        // If we have a current variant, only show matching non-size options
        if (currentSignature) {
          var sigParts = [];
          for (var j = 0; j < product.options.length; j++) {
            if (j === sizeIdx) continue;
            var val = v.options ? v.options[j] : v['option' + (j + 1)];
            sigParts.push(val || '');
          }
          if (sigParts.join('|') !== currentSignature) return false;
        }

        // Get size value (support both formats)
        var sizeVal = v.options ? v.options[sizeIdx] : v['option' + (sizeIdx + 1)];
        if (seenSizes.has(sizeVal)) return false;
        seenSizes.add(sizeVal);
        return true;
      });

      if (matchingVariants.length === 0) { this.showState('error'); return; }

      // Only one size available — add it directly without showing the modal
      if (matchingVariants.length === 1) {
        this.handleSizeSelection(matchingVariants[0]);
        return;
      }

      this.sizesContainer.innerHTML = '';
      var self = this;
      matchingVariants.forEach(function(variant) {
        var sizeVal = variant.options ? variant.options[sizeIdx] : variant['option' + (sizeIdx + 1)];
        var btn = document.createElement('button');
        btn.className = 'size-modal__size-btn';
        btn.textContent = sizeVal;
        btn.addEventListener('click', function() { self.handleSizeSelection(variant); });
        self.sizesContainer.appendChild(btn);
      });

      if (this.titleEl) this.titleEl.textContent = product.title;
      this.showState('sizes');
    }

    normalizeProperties(properties) {
      if (!properties) return {};
      var normalized = {};

      if (Array.isArray(properties)) {
        for (var item of properties) {
          if (Array.isArray(item) && item.length >= 2) normalized[String(item[0])] = String(item[1]);
        }
        return normalized;
      }

      for (var [key, value] of Object.entries(properties)) {
        if (/^\d+$/.test(key) && typeof value === 'string' && value.includes(',')) {
          var commaIdx = value.indexOf(',');
          normalized[value.substring(0, commaIdx).trim()] = value.substring(commaIdx + 1).trim();
        } else {
          normalized[key] = value;
        }
      }
      return normalized;
    }

    extractAddonVariantId() {
      if (!this.currentProperties) return null;
      var props = this.currentProperties;

      if (Array.isArray(props)) {
        for (var item of props) {
          if (Array.isArray(item) && item[0] === '_tib_addon_variant') return parseInt(item[1].split(':')[0], 10);
        }
      } else {
        if (props['_tib_addon_variant']) return parseInt(props['_tib_addon_variant'].split(':')[0], 10);
        for (var [key, value] of Object.entries(props)) {
          if (typeof value === 'string' && value.includes('_tib_addon_variant,')) {
            var parts = value.split(',');
            if (parts.length >= 2) return parseInt(parts[1].split(':')[0], 10);
          }
        }
      }
      return null;
    }

    async handleSizeSelection(variant) {
      if (this.isProcessing) return;
      this.isProcessing = true;

      var buttons = this.sizesContainer.querySelectorAll('button');
      buttons.forEach(function(btn) { btn.disabled = true; });
      this.showState('loading');

      try {
        var properties = this.normalizeProperties(this.currentProperties);

        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variant.id, quantity: 1, properties: properties })
        });
        if (!res.ok) throw new Error('Add failed');

        var addonVariantId = this.extractAddonVariantId();
        if (addonVariantId) {
          await this.incrementAddonInCart(addonVariantId);
        }

        this.close();
        await this.refreshCart();
      } catch (e) {
        console.error('Failed to add to cart:', e);
        this.showState('error');
        buttons.forEach(function(btn) { btn.disabled = false; });
      } finally {
        this.isProcessing = false;
      }
    }

    async incrementAddonInCart(addonVariantId) {
      try {
        var cartRes = await fetch('/cart.js');
        var cart = await cartRes.json();
        var existing = cart.items.find(function(item) { return item.variant_id === addonVariantId; });

        if (existing) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: existing.key, quantity: existing.quantity + 1 })
          });
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: addonVariantId, quantity: 1 })
          });
        }
      } catch (e) {
        console.error('Failed to handle addon:', e);
      }
    }

    async refreshCart() {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'size-selector-modal' });
      }

      var cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (!cartDrawer || !cartDrawer.getSectionsToRender) return;

      var sectionIds = cartDrawer.getSectionsToRender().map(function(s) { return s.id; });
      try {
        var res = await fetch('/cart.js?sections=' + sectionIds.join(','));
        var data = await res.json();
        if (data.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data);
        }
      } catch (e) {
        location.reload();
      }
    }
  }

  customElements.define('size-selector-modal', SizeSelectorModal);
}
